// Which way a belt part turns between arriving one way and leaving another.
//
// Kept separate from the renderer and free of Three.js so it can be tested:
// getting it wrong shows up as a lane that visibly kinks rather than turns.
// Which parts *are* corners is the engine's answer now — `belt.bend` and
// `belt.feeders` on the square's facts — so this holds only the arithmetic the
// preview of a run not yet on the board still has to do for itself.

import type { Direction } from "src/shared/board";

export interface Corner {
  /** The direction material is travelling as it arrives. */
  inbound: Direction;
  /** Which way the lane turns, seen from above. */
  hand: "left" | "right";
}

/**
 * The turn a part makes between arriving one way and leaving another, or null
 * when it does not make one.
 *
 * The *preview* of a run being drawn asks this from the run itself rather than
 * from the network, which is not on the board yet. One rule about what a bend
 * is, the same one `engine/api/beltShape` applies: two would let the ghost
 * promise an arc the laid part does not draw.
 */
export function bendBetween(inbound: Direction, outbound: Direction): Corner | null {
  if (inbound === outbound) return null;
  // A lane that doubles back on itself is not a quarter turn, and there is no
  // sensible arc for it — leave it straight and let the arrows say what is
  // happening.
  if (outbound === opposite(inbound)) return null;

  return { inbound, hand: (outbound - inbound + 4) % 4 === 1 ? "right" : "left" };
}

/** The reverse of a direction. Local, so this file needs nothing from the rules. */
const opposite = (direction: Direction): Direction => ((direction + 2) % 4) as Direction;
